/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBreakdown,
  selectDateComparison,
  selectSummaryAlignmentToggle,
  selectSymmetricComparisonToggle,
  selectSummaryOtb,
  selectSummaryCustomDate,
  selectIsDetailed,
  selectSummaryEndDate,
  selectSummaryBookingDate,
  selectSummaryStartDate,
  selectSummaryVisualToggle,
  selectPaceVisualIsDisplay,
  selectRevenueSetIsDisplay,
  selectCxlSetIsDisplay,
  selectSymmetricComparisonIsDisplay,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import {
  selectPaceHorizon,
  selectPaceVariable,
  selectTargetDate,
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectEnableHotelGroups,
  selectSummaryForecastFilter,
  selectLocalCurrency,
} from 'modules/dashboard/selectors';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import { generateSummaryShareUrl } from 'modules/dashboard/components/tab-container/inner-filter/functions';

/**
 * Custom hook to synchronize the summary share URL with filter and visual display state
 */
const useSummaryShareUrl = () => {
  const dispatch = useDispatch();
  const chartBreakdown = useSelector(selectBreakdown);
  const dateComparison = useSelector(selectDateComparison);
  const bookingDate = useSelector(selectSummaryBookingDate);
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const paceVariable = useSelector(selectPaceVariable);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const isDetailed = useSelector(selectIsDetailed);
  const endDate = useSelector(selectSummaryEndDate);
  const startDate = useSelector(selectSummaryStartDate);
  const customBooking = useSelector(selectSummaryOtb);
  const alignmentToggle = useSelector(selectSummaryAlignmentToggle);
  const customDate = useSelector(selectSummaryCustomDate);
  const visualToggle = useSelector(selectSummaryVisualToggle);
  const paceVisualIsDisplay = useSelector(selectPaceVisualIsDisplay);
  const revenueSetIsDisplay = useSelector(selectRevenueSetIsDisplay);
  const cxlSetIsDisplay = useSelector(selectCxlSetIsDisplay);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const forecastToggle = useSelector(selectSummaryForecastFilter);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const localCurrency = useSelector(selectLocalCurrency);
  // Triggers when filter values dependencies changes
  useEffect(() => {
    if (
      !dateComparison &&
      !chartBreakdown &&
      !bookingDate &&
      !targetDate &&
      !hotelId &&
      !paceHorizon &&
      !paceVariable
    )
      return;
    // Generate summary widget filter url
    const summaryUrl = generateSummaryShareUrl(
      pmsSyncToggle,
      hotelId,
      targetDate,
      dateComparison,
      paceVariable,
      visualFilters,
      paceHorizon,
      startDate,
      endDate,
      customDate,
      isDetailed,
      chartBreakdown,
      customBooking,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      hotelId,
      enableHotelGroups,
      visualToggle,
      paceVisualIsDisplay,
      revenueSetIsDisplay,
      cxlSetIsDisplay,
      symmetricComparisonIsDisplay,
      forecastToggle,
      enableSecondaryHotelGroups,
      hotelFocusOn,
      normalizedViewToggle,
      localCurrency
    );
    //
    dispatch(summaryActions.setSummaryUrl(summaryUrl));
  }, [
    pmsSyncToggle,
    chartBreakdown,
    bookingDate,
    visualFilters,
    dateComparison,
    alignmentToggle,
    targetDate,
    hotelId,
    paceHorizon,
    paceVariable,
    symmetricComparisonToggle,
    isDetailed,
    customBooking,
    startDate,
    endDate,
    customDate,
    enableHotelGroups,
    visualToggle,
    paceVisualIsDisplay,
    revenueSetIsDisplay,
    cxlSetIsDisplay,
    symmetricComparisonIsDisplay,
    forecastToggle,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    normalizedViewToggle,
    localCurrency,
  ]);
};

export default useSummaryShareUrl;
